/**
 * Google tag wiring.
 *
 * NQ Springs already run a GTM container (GTM-N5PSCQ3J) whose Google tag carries both
 * G-MLP2LKW0MQ (GA4) and AW-10811787212 (Ads). So we load the container and nothing else -
 * GA4 page views keep coming from their own tag, no double counting.
 *
 * Their old Ads conversion tags were triggered by WordPress element-visibility/click rules
 * that don't exist on this site, so we fire the conversions ourselves against the same
 * conversion IDs, on *successful submission* rather than on click: a half-filled form or a
 * validation error can never count as a lead. Everything is also pushed to window.dataLayer,
 * so they can trigger anything else off `enquiry_submitted`, `phone_click` and
 * `podium_message_sent` from inside GTM without touching this code. Podium chats also send
 * a GA4 `submit_lead_form` event with lead_source=podium_chat.
 *
 * Nothing loads on preview/localhost, so testing can't pollute their Ads or GA4 data.
 */
const GTM_ID = process.env.REACT_APP_GTM_ID || "";
const GA4_ID = process.env.REACT_APP_GA4_ID || "";
const ADS_ID = process.env.REACT_APP_GOOGLE_ADS_ID || "";

const LABELS = {
  contact_form: process.env.REACT_APP_ADS_LABEL_CONTACT || "",
  enquiry: process.env.REACT_APP_ADS_LABEL_ENQUIRY || "",
  phone: process.env.REACT_APP_ADS_LABEL_PHONE || "",
};

const isLiveHost = () => {
  const h = window.location.hostname;
  if (h === "localhost" || h.endsWith(".preview.emergentagent.com")) {
    return process.env.REACT_APP_ANALYTICS_DEBUG === "true";
  }
  return true;
};

let started = false;

export function initAnalytics() {
  if (started || !isLiveHost()) return;
  started = true;

  window.dataLayer = window.dataLayer || [];
  window.gtag = window.gtag || function gtag() { window.dataLayer.push(arguments); };

  const script = document.createElement("script");
  script.async = true;

  if (GTM_ID) {
    window.dataLayer.push({ "gtm.start": Date.now(), event: "gtm.js" });
    script.src = `https://www.googletagmanager.com/gtm.js?id=${GTM_ID}`;
    document.head.appendChild(script);
  } else if (GA4_ID || ADS_ID) {
    // No container: fall back to loading gtag.js ourselves.
    script.src = `https://www.googletagmanager.com/gtag/js?id=${GA4_ID || ADS_ID}`;
    document.head.appendChild(script);
    window.gtag("js", new Date());
    if (GA4_ID) window.gtag("config", GA4_ID);
    if (ADS_ID) window.gtag("config", ADS_ID, { allow_enhanced_conversions: true });
  }

  watchPhoneClicks();
  watchPodium();
}

let lastPath = null;

/** GA4 only counts full page loads, so report in-app page changes ourselves. */
export function trackPageChange(path) {
  if (path === lastPath) return;
  const first = lastPath === null;
  lastPath = path;
  if (first || !started || path.startsWith("/admin")) return; // initial load is counted by the tag
  if (!window.gtag || !GA4_ID) return;
  // Wait for the new page to set its title.
  setTimeout(() => {
    window.gtag("event", "page_view", {
      send_to: GA4_ID,
      page_location: window.location.href,
      page_path: path,
      page_title: document.title,
    });
  }, 600);
}

/** Push an event for GA4 / GTM triggers. */
export function trackEvent(name, params = {}) {
  window.dataLayer = window.dataLayer || [];
  window.dataLayer.push({ event: name, ...params });
}

function adsConversion(label, extra = {}) {
  if (!window.gtag || !ADS_ID || !label) return;
  window.gtag("event", "conversion", { send_to: `${ADS_ID}/${label}`, ...extra });
}

/**
 * A completed enquiry. `source` picks which of their existing Ads conversion actions to
 * report against, and email/phone go along for Enhanced Conversions (hashed by gtag,
 * never stored by us).
 */
export function trackEnquiryConversion({ source, email, phone, value } = {}) {
  trackEvent("enquiry_submitted", { enquiry_source: source || "unknown" });

  if (email || phone) {
    if (window.gtag) {
      window.gtag("set", "user_data", {
        ...(email ? { email: String(email).trim().toLowerCase() } : {}),
        ...(phone ? { phone_number: String(phone).replace(/[^\d+]/g, "") } : {}),
      });
    }
  }

  const label = source === "contact_form" ? LABELS.contact_form : LABELS.enquiry;
  adsConversion(label, {
    ...(value ? { value, currency: "AUD" } : {}),
    enquiry_source: source || "unknown",
  });
}

/** Every tel: link on the site, without having to touch each component. */
function watchPhoneClicks() {
  document.addEventListener(
    "click",
    (e) => {
      const link = e.target && e.target.closest && e.target.closest('a[href^="tel:"]');
      if (!link) return;
      trackEvent("phone_click", { phone_number: link.getAttribute("href").replace("tel:", "") });
      adsConversion(LABELS.phone);
    },
    true
  );
}

const PODIUM_SENT_KEY = "nq_podium_lead_sent";

/** Podium's own hook: their widget calls window.PodiumEventsCallback(event, properties). */
export function handlePodiumEvent(event, properties = {}) {
  if (event === "Bubble Clicked") {
    trackEvent("podium_chat_opened", {}); // engagement only, not a lead
    return;
  }
  if (event !== "Conversation Started") return;
  // One lead per browser session, however many messages they send.
  if (sessionStorage.getItem(PODIUM_SENT_KEY)) return;
  sessionStorage.setItem(PODIUM_SENT_KEY, "1");

  const phone = properties["customer-phone"];
  if (phone && window.gtag) {
    window.gtag("set", "user_data", { phone_number: String(phone).replace(/[^\d+]/g, "") });
  }
  trackEvent("podium_message_sent", { lead_source: "podium_chat" });
  adsConversion(LABELS.enquiry, { enquiry_source: "podium_webchat" });
  if (window.gtag && GA4_ID) {
    window.gtag("event", "submit_lead_form", { send_to: GA4_ID, lead_source: "podium_chat" });
  }
}

function watchPodium() {
  const previous = window.PodiumEventsCallback;
  window.PodiumEventsCallback = (event, properties) => {
    handlePodiumEvent(event, properties);
    if (typeof previous === "function") previous(event, properties);
  };
}
